import type { ChangeEvent } from 'react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Card, CardContent, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { black, cardBorder } from '../../constants/Colors';
import { cardContentPadding, sizeBelowTenMb } from '../../constants/Location';

import CustomImageUploader from './CustomImageUploader';
import CustomSelectField from './CustomSelectField';
import CustomTextfield from './CustomTextfield';

import type { Control, FieldErrors, UseFormWatch } from 'react-hook-form';

export interface RegisterArticleForm {
  media: string | number;
  title: string;
  outline: string;
  feature: string;
  menu: string;
  caseStudy: string;
  message: string;
  bgImg: File | null;
}

const styles = {
  card: {
    boxShadow: 0,
    margin: { sm: '12px 24px 24px 24px', xs: '16px' }
  },
  cardBorder,
  cardContentPadding,
  itemTitle: {
    '@media screen and (max-width:960px)': {
      fontSize: '18px'
    },
    color: black,
    fontSize: '21px',
    fontWeight: 700,
    mb: '16px'
  },
  title: {
    '@media screen and (max-width:960px)': {
      fontSize: '18px',
      mb: '16px'
    },
    color: black,
    fontSize: '24px',
    fontWeight: 700,
    mb: '24px'
  }
};

interface Props {
  control: Control<RegisterArticleForm, any>;
  watch: UseFormWatch<RegisterArticleForm>;
  errors: FieldErrors<Record<string, any>>;
  mediaList: Array<{ value: string; option: string }>;
  mediaChangeDisabled: boolean;
  changeUploadFile: (e: ChangeEvent<HTMLInputElement>) => void;
  preview: string;
  s3File: FileList | null;
  firstSubmit?: boolean;
  disabled: boolean;
}

const RegisterArticleCard: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const {
    control,
    watch,
    errors,
    mediaList,
    mediaChangeDisabled,
    changeUploadFile,
    preview,
    s3File,
    firstSubmit,
    disabled
  } = props;

  const [sizeError, setSizeError] = useState<string | null>(null);

  useEffect(() => {
    if (s3File && s3File.length > 0) {
      sizeBelowTenMb(s3File[0].size)
        ? setSizeError(t('validation.error.imageSize'))
        : setSizeError(null);
    } else {
      setSizeError(null);
    }
  }, [s3File]);

  const [imgRequiredError, setImgRequiredError] = useState<string | null>(null);

  useEffect(() => {
    preview === ''
      ? setImgRequiredError(t('validation.error.required'))
      : setImgRequiredError(null);
  }, [preview]);

  return (
    <>
      <Card sx={[styles.card, styles.cardBorder]}>
        <CardContent sx={styles.cardContentPadding}>
          <Typography sx={styles.title}>
            {id ? t('mypage.article.edit') : t('mypage.article.create')}
          </Typography>
          <Controller
            render={({ field }) => (
              <CustomSelectField
                {...field}
                labelText={t('mypage.article.mediaSelect')}
                placeholder={t('form.empty')}
                isRequired
                requiredText={t('form.required')}
                error={Boolean(errors.media)}
                helperText={errors.media?.message}
                defaultValue={''}
                list={mediaList}
                blank
                width="article"
                disabled={mediaChangeDisabled || disabled}
              />
            )}
            control={control}
            name="media"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText={t('mypage.article.articleTitle')}
                isRequired
                requiredText={t('form.required')}
                placeholder={t('mypage.article.titlePlaceholder')}
                sx={{ marginTop: '0px' }}
                error={Boolean(errors.title)}
                helperText={errors.title?.message}
                multiline
                rows={2}
                fieldType="textareaRow2"
                width="article"
                value={watch('title')}
                disabled={disabled}
              />
            )}
            control={control}
            name="title"
          />
          <Controller
            render={({ field }) => (
              <CustomImageUploader
                type="article"
                preview={preview}
                isRequired
                requiredText={t('form.required')}
                error={Boolean(errors.bgImg)}
                helperText={errors.bgImg?.message}
                otherError={firstSubmit ? sizeError ?? imgRequiredError : null}
                onChange={(value: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(value);
                  changeUploadFile(value);
                }}
                disabled={disabled}
              />
            )}
            control={control}
            name="bgImg"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText={t('mypage.article.outline')}
                width="article"
                fieldType="textareaRow6"
                multiline
                rows={6}
                isRequired={false}
                error={Boolean(errors.outline)}
                helperText={errors.outline?.message}
                disabled={disabled}
                guideText={t('mypage.article.outlineGuideText')}
              />
            )}
            control={control}
            name="outline"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText={t('mypage.article.feature')}
                width="article"
                fieldType="textareaRow6"
                multiline
                rows={6}
                isRequired={false}
                error={Boolean(errors.feature)}
                helperText={errors.feature?.message}
                disabled={disabled}
                guideText={t('mypage.article.featureGuideText')}
              />
            )}
            control={control}
            name="feature"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText={t('mypage.article.menu')}
                width="article"
                fieldType="textareaRow6"
                multiline
                rows={6}
                isRequired={false}
                error={Boolean(errors.menu)}
                helperText={errors.menu?.message}
                disabled={disabled}
                guideText={t('mypage.article.menuGuideText')}
              />
            )}
            control={control}
            name="menu"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText={t('mypage.article.caseStudy')}
                width="article"
                fieldType="textareaRow6"
                multiline
                rows={6}
                isRequired={false}
                error={Boolean(errors.caseStudy)}
                helperText={errors.caseStudy?.message}
                disabled={disabled}
                guideText={t('mypage.article.caseStudyGuideText')}
              />
            )}
            control={control}
            name="caseStudy"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                labelText={t('mypage.article.message')}
                width="article"
                fieldType="textareaRow6"
                multiline
                rows={6}
                isRequired={false}
                error={Boolean(errors.message)}
                helperText={errors.message?.message}
                disabled={disabled}
                guideText={t('mypage.article.messageGuideText')}
              />
            )}
            control={control}
            name="message"
          />
        </CardContent>
      </Card>
    </>
  );
};

export default RegisterArticleCard;
